import {useCallback} from "react";

import type {AIGuideMetadata} from "@khan/guide-types";

import {useSendGuideItem} from "../internal/use-send-guide-item.ts";

interface SendGuideEventFnInput {
    params?: Record<any, any>;
    assignmentID?: string;
    customPrompt?: string;
    onResponseComplete?: (metadata: AIGuideMetadata, answer: string) => void;
}

interface SendGuideEventFn {
    /**
     * Send the initial_load event, with or without a custom prompt.
     */
    (
        name: "initial_load",
        input: {customPrompt?: string; assignmentID?: string},
    ): Promise<void>;

    /**
     * Send the create-assignment-success event.
     */
    (
        name: "create_assignments_success",
        input: {
            params: {groupName: string};
        },
    ): Promise<void>;

    /**
     * Send the resume_conversation event.
     */
    (
        name: "resume_conversation",
        input: {
            onResponseComplete: (metadata: AIGuideMetadata) => void;
        },
    ): Promise<void>;

    /**
     * Send the content_selection event to indicate that the user selected new
     * content in the browser extension.
     */
    (name: "content_selection"): Promise<void>;

    /**
     * Send the submit_assignment event.
     */
    (name: "submit_assignment"): Promise<void>;

    /**
     * Send the generate_dynamic_action_bubbles event
     */
    (
        name: "generate_dynamic_action_bubbles",
        input: {
            params: {interactionId: string};
        },
    ): Promise<void>;

    // Add other overloads here as needed. For example:
    // (name: "some_other_event"
    //  input: {
    //     params?: {a: number, b: string},
    //     customPrompt?: string,
    //     onResponseComplete?: (metadata: AIGuideMetadata, answer: string) => void,
    // }): Promise<void>;
}

/**
 * A hook to send events to the ai guide.
 *
 * @returns A function to send events to the ai guide. The event to send is
 * defined by an object containing the event name and parameters for that
 * event. Use the `Events` API to generate a specific known event.
 */
export const useSendGuideEvent = (): SendGuideEventFn => {
    const sendGuideItem = useSendGuideItem();

    const sendEvent: SendGuideEventFn = useCallback(
        (name: string, input: SendGuideEventFnInput = {}) => {
            return sendGuideItem(
                "event",
                {
                    event: {
                        name: name,
                        params: input.params ?? {},
                    },
                },
                {
                    assignmentID: input.assignmentID,
                    customPrompt: input.customPrompt,
                    onResponseComplete: input.onResponseComplete,
                },
            );
        },
        [sendGuideItem],
    );
    return sendEvent;
};
